import { Controller } from 'stimulus';
import toastr from 'toastr';
import html2pdf from 'html2pdf.js';
import logo from '../images/logos/logo-color-email.png';
import ucdLogo from '../images/logos/logo-ucd-grey.png';

export default class extends Controller {
  static targets = [
    'yearSelector',
    'breakdownContent',
    'totalHoursOfHours',
    'totalHoursProgress',
    'yearIncrementor',
    'yearDecrementor',
    'carouselDot',
    'categoryHoursOfHours',
    'totalHoursOfHoursStatus',
    'categoryHoursList',
    'mobileTable',
    'mobileEventTable',
    'userYearDetail'
  ]

  static values = {
    hoursBreakdown: Object,
    yearData: Object,
    hoursByYear: Object,
    eventRowData: Object,
  }

  get years() {
    return Object.keys(this.hoursByYearValue);
  }

  connect() {
    this.toClone = this.hoursBreakdownRowTarget;
    this.previousYearCheck(new Date().getFullYear() - 1);
    this.setYear(new Date().getFullYear());
  }

  setYear(year) {
    this.yearSelectorTarget.innerHTML   = year;
    this.userYearDetailTarget.innerHTML = year;
    this.setValues(year);
    this.setTotalHours(year);
    this.setHoursBreakdown(year);
    this.setMobileSlideContent(year);
  }

  rowHeaders() {
    return [
      "Minimum Hours Required (Opening Balance)",
      "Pro Rata",
      "Shortfall Applied (from previous year{s})",
      "Minimum Hours Required",
      "Recorded Hours",
      "Remaining Hours"
    ]
  }

  setMobileSlideContent(year) {    
    this.mobileTableTarget.innerHTML = '';
    let categoryTotals = this.hoursByYearValue[year] || {};

    let content = ``;
    for (const [key, value] of Object.entries(categoryTotals)) {
      let rows = ``;
      const categoryData = value || {};
      const categoryDataMissing = value == null;
      this.rowHeaders().forEach(header => {
        let rowHeader = header;

        if(header === 'Shortfall Applied (from previous year{s})'){
          rowHeader = 'Shortfall';
        }else if(header === 'Minimum Hours Required (Opening Balance)'){
          rowHeader = 'Annual CPD Hour Requirement';
        }

        rows += `<tr>
          <td class="cpd-my-table__td-left-poppins">${rowHeader}</td>
          <td class="cpd-my-table__td-100">${categoryDataMissing ? '' : (categoryData[header] ?? 0)}</td>
        </tr>`;

      });

      let table = `
      <div class="categories-table-mobile-content__table-header">${key}</div>
      <table class="cpd-my-table__table">
        <thead>
          <tr>
            <th class="cpd-my-table__td-left">Hours requirements</th>
            <th>Total Hours</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>`;

      content += table;

    }

    this.mobileTableTarget.innerHTML += content;
  }

  setHoursBreakdown(year) {
    const width = window.innerWidth;
    let tableHeaders = document.getElementById('cpdHoursBreakdownHeaders');
    let tableBody = document.getElementById('cpdHoursBreakdown');
    tableBody.innerHTML = "";
    tableHeaders.innerHTML = "";

    let categories = [
      ...new Set([
        ...this.hoursBreakdownValue[year]
            .map(item => item?.Categories || {})
            .flatMap(categories => Object.keys(categories)),
        ...Object.keys(this.hoursByYearValue[year]).filter(
            key => !this.hoursBreakdownValue[year]
                .some(item => item?.Categories && key in item.Categories)
        )
      ])
    ];

    const totalHourCategoryValue = 'Total Hours';
    if (!categories.includes(totalHourCategoryValue)) {
      categories.unshift(totalHourCategoryValue);
    }
    
    // Order categories to match the order of hoursByYearData keys
    const hoursByYearKeys = Object.keys(this.hoursByYearValue[year]);
    categories = categories.sort((a, b) => {
      return hoursByYearKeys.indexOf(a) - hoursByYearKeys.indexOf(b);
    });

    let eventHeaderCell = document.createElement('th');
    eventHeaderCell.textContent = width <= 480 ? 'Returned Events' : 'Event';
    eventHeaderCell.style.textAlign = 'left';
    eventHeaderCell.style.width = "480px";
    eventHeaderCell.style.minWidth = "200px";
    tableHeaders.append(eventHeaderCell);

    // Create Total column first
    let totalHeaderCell = document.createElement('th');
    totalHeaderCell.textContent = 'Total';
    tableHeaders.append(totalHeaderCell);

    // Filter out Total Hours and iterate remaining categories
    categories
      .filter(header => header !== 'Total Hours')
      .forEach(function(header) {
        let rowHeaderCell = document.createElement('th');
        rowHeaderCell.textContent = header;
        tableHeaders.append(rowHeaderCell);
      });

    this.hoursBreakdownValue[year].forEach(function(event) {
      let row = document.createElement('tr');
      row.id = `eventRowId-${event.Id}`;
      let categoryTotal = ``;

      categories.forEach(function(category) {
        // Access the category value from record.Categories or default to 0 if not found
        let cellValue = category === 'Total Hours' ? event.Hours : event.Categories?.[category] || 0;

        // Add the cell value to the row
        categoryTotal += `<td class="cpd-my-table__td-center">${cellValue}</td>`;
      });

      // Create the row HTML with event details
      row.innerHTML = `
    <td class="cpd-my-table__td-left-poppins">
      <p class='cpd-my-table__event-name'>${event['Name']}</p>
      <p class='cpd-my-table__event-name-details'>Accreditations Ref No: ${event['Reference No']}</p>
      <p class='cpd-my-table__event-name-details'>Type: ${event['Type']}</p>
      <p class='cpd-my-table__event-name-details'>Event Provider: ${event['Provider']}</p>
      <p class='cpd-my-table__event-name-details'>Date: ${event['Date']}</p>
      <button class="cpd-my-hours__view-links" data-action='my-hours#openSlide' data-slide-id="events" data-event-year=${year} data-event-id=${event.Id}>
        View more <img src="/packs/media/right-arrow-orange.svg">
      </button>
    </td>` + categoryTotal;

      // Conditionally add the delete icon column if the event is editable
      if (event['Editable'] !== 0) {
        row.innerHTML += `<td style="width: 48px;" class='actionData cpd-my-table__td-center'>
      <svg class='cpd-my-table__delete-icon' data-action='click->my-hours#deleteEvent' data-row-id='eventRowId-${event.Id}' data-url='/api/v1/cpd/${document.URL.match('users\/cpd\/(.*).lxp=true')[1]}/${event['Accredited Event Id']}' width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6V18.75C18 19.993 16.973 21 15.731 21H8.231C6.988 21 6 19.993 6 18.75V6" stroke="#06243C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M19.5 6H4.5" stroke="#06243C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 3H14" stroke="#06243C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14 10V17" stroke="#06243C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 17V10" stroke="#06243C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg></td>`;
      } else {
        row.innerHTML += `<td style="width: 48px;" class='actionData cpd-my-table__td-center'></td>`;
      }

      // Append the row to the table body
      tableBody.appendChild(row);
    }, this);
  }

  setTotalHours(year) {
    let totalHours = this.yearDataValue[year]['minimum_hours_required'];
    let remainingHours = this.yearDataValue[year]['remaining_hours'];
    let recordedHours = this.yearDataValue[year]['recorded_hours'];
    let barWidth = (totalHours - remainingHours) / parseFloat(totalHours) * 100;

    if (remainingHours > 0) {
      this.totalHoursOfHoursTarget.innerHTML = `<span class="cpd-my-hours__hours-complete-recorded-hours">${totalHours - remainingHours}/</span><span class="cpd-my-hours__hours-complete-total-hours">${totalHours}</span> Required Hours complete`;
      this.totalHoursOfHoursStatusTarget.innerHTML = `${remainingHours} remaining`;
      this.almostFullBar(barWidth, this.totalHoursProgressTarget);
    } else {
      this.fullBar(this.totalHoursOfHoursTarget, this.totalHoursProgressTarget, recordedHours, totalHours, 'TOTAL');
      this.totalHoursOfHoursStatusTarget.innerHTML = `<img src="/packs/media/green-circular-tick-large.svg"> Complete`;
    }
  }

  setValues(year) {
    const width = window.innerWidth;
    this.categoryHoursListTarget.innerHTML = '';
    let tableHeaders = document.getElementById('cpdTableHeaders');
    tableHeaders.innerHTML = "";
    let tableBody = document.getElementById('cpdTableBody');
    tableBody.innerHTML = "";
    let jsonData = this.hoursByYearValue[year] || {}

    let columnHeaders = Object.keys(jsonData);
    let hoursHeaderCell = document.createElement('th');
    hoursHeaderCell.textContent = width <= 480 ? 'Hours requirements' : 'Hours';
    hoursHeaderCell.style.width = "480px";
    hoursHeaderCell.style.minWidth = "200px";
    hoursHeaderCell.style.textAlign = 'left';
    let totalHeaderCell = document.createElement('th');
    totalHeaderCell.textContent = width <= 480 ? 'Total Hours' : 'Total';
    tableHeaders.append(hoursHeaderCell);
    tableHeaders.append(totalHeaderCell);

    columnHeaders.forEach(function(header){
      let rowHeaderCell = document.createElement('th');
      rowHeaderCell.textContent = header;
      tableHeaders.append(rowHeaderCell);
    })

    let yearDataValue = this.yearDataValue[year] || {};

    if(columnHeaders.length == 0){
      tableBody.innerHTML = '<tr><td class="cpd-my-table__no-data">No hours found</td></tr>';
    }else{
      let actionHeaderCell = document.createElement('th');
      actionHeaderCell.classList.add('actionHeader');
      tableHeaders.append(actionHeaderCell);  
    }

    this.rowHeaders().forEach(function(header) {
      let row = document.createElement('tr');

      let rowHeader = header;

      if(header === 'Shortfall Applied (from previous year{s})'){
        rowHeader = 'Shortfall';
      }else if(header === 'Minimum Hours Required (Opening Balance)'){
        rowHeader = 'Annual CPD Hour Requirement';
      }

      let rowHeaderCell = document.createElement('td');
      rowHeaderCell.textContent = rowHeader;
      rowHeaderCell.classList.add('cpd-my-table__row-headers');
      row.appendChild(rowHeaderCell);

      let rowTotalCell = document.createElement('td');
      rowTotalCell.textContent = yearDataValue[rowHeader.toLowerCase().split(' ').join('_')];
      row.appendChild(rowTotalCell);

      columnHeaders.forEach(function(category) {
        let cell = document.createElement('td');
        const categoryData = jsonData[category];
        cell.textContent = categoryData ? categoryData[header] ?? 0 : '';
        row.appendChild(cell);
      });

      let cell = document.createElement('td');
      cell.style.width = '48px';
      cell.classList.add('actionData');
      row.appendChild(cell);

      tableBody.appendChild(row);
    });

    let completedCategories = ``;
    let nonCompletedCategories = ``;
    let categoriesCompleted = 0;

    columnHeaders.forEach(function(category) {
      const categoryData = jsonData[category];

      if(categoryData && Number(categoryData['Recorded Hours']) >= Number(categoryData['Minimum Hours Required'])){
        categoriesCompleted = categoriesCompleted + 1;

        completedCategories += `<div class="cpd-my-hours__category-hours-bar"><img src='/packs/media/green-circular-tick.svg'></img><div class="cpd-my-hours__category-hours-bar-text">${category}</div></div>`;
      }else{
        nonCompletedCategories += `<div class="cpd-my-hours__category-hours-bar-pending"><img src='/packs/media/unticked.svg'></img><div class="cpd-my-hours__category-hours-bar-text-pending">${category}</div></div>`;
      }
    })

    this.categoryHoursListTarget.innerHTML = completedCategories + nonCompletedCategories;
    this.setCategoryHours(categoriesCompleted, columnHeaders.length);
  }

  populateEventSlide(eventId, year){
    const data = this.hoursBreakdownValue[year];
    const event = data.filter(event => event.Id.toString() === eventId)[0];

    this.mobileEventTableTarget.innerHTML = '';

    let rows = '';
    if(event.Categories === undefined){
      rows = '<tr><td class="events-table-mobile-content--open__no-event-categories">No categories found</td><td></td></tr>';
    }else {
      for (const [key, value] of Object.entries(event.Categories)) {
        if(key === 'Total Hours') continue;

        rows += `<tr>
          <td class="cpd-my-table__td-left-poppins">${key}</td>
          <td class="cpd-my-table__td-100">${value}</td>
        </tr>`;
      }
    }

    let table = `
    <div class="events-table-mobile-content--open__event-details-container">
      <div class="events-table-mobile-content--open__event-details-icon">
        <svg data-action='click->my-hours#deleteEvent' data-row-id='eventRowId-${event.Id}' data-url='/api/v1/cpd/${document.URL.match('users\/cpd\/(.*).lxp=true')[1]}/${event.Id}' width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6V18.75C18 19.993 16.973 21 15.731 21H8.231C6.988 21 6 19.993 6 18.75V6" stroke="#FF5E5E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M19.5 6H4.5" stroke="#FF5E5E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 3H14" stroke="#FF5E5E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14 10V17" stroke="#FF5E5E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 17V10" stroke="#FF5E5E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div>
        <p class='cpd-my-table__event-slide-name'>${event['Name']}</p>
        <p class='cpd-my-table__event-slide-name-details'>Accreditations Ref No: ${event['Reference No']}</p>
        <p class='cpd-my-table__event-slide-name-details'>Type: ${event['Type']}</p>
        <p class='cpd-my-table__event-slide-name-details'>Event Provider: ${event['Provider']}</p>
        <p class='cpd-my-table__event-slide-name-details'>Date: ${event['Date']}</p>
      </div>
    </div>
    <div class="events-table-mobile-content--open__total-hours-section">
      <p class="events-table-mobile-content--open__total-hours-section-key">Total Hours recorded</p>
      <p class="events-table-mobile-content--open__total-hours-section-value">${event.Categories === undefined ? 0 : event.Categories['Total Hours']}</p>
    </div>
    <table class="cpd-my-table__table">
      <thead>
        <tr>
          <th class="cpd-my-table__td-left">Category</th>
          <th>Hours recorded</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>`;

    this.mobileEventTableTarget.innerHTML = table;
  }

  deleteEvent(event) {
    if(confirm("Are you sure you want to delete this event?")){
      const url          = event.currentTarget.dataset.url;
      const rowId        = event.currentTarget.dataset.rowId;
      const fetchOptions = Object.assign(this.defaultOptions(), { method: 'DELETE' });

      document.getElementById("loader-anim").style.display = "block";

      fetch(url, fetchOptions)
        .then(response => {
          document.getElementById("loader-anim").style.display = "none";

          if (response.status === 204) {
            let row = document.getElementById(rowId);
            row.style.display = 'none';
            document.getElementById(`eventsSlide`).classList.remove(`events-table-mobile-content--open`);
            return;
          } else if (response.status === 400) {
            return response.json().then(json => {
              throw new Error(json.error);
            });
          } else {
            throw new Error('Could not delete event at this time. Please try again.');
          }
        })
        .catch(error => {
          document.getElementById("loader-anim").style.display = "none";
          
          toastr.error(error.message, 'Error', {
            timeOut: 0,
            extendedTimeOut: 0,
            closeButton: true,
            allowHtml: true,
            positionClass: "toast-top-center"
          });
        })
      }
  }

  downloadAsPDF() {
    let cpdMyTableHtml = $('#cpdMyTable').html();
    let hoursBreakDownTableHtml = $('#hoursBreakDownTable').html();
    let userDetailsHtml = $('#userDetails').html();

    let htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Category Requirements Breakdown</title>
      <style>
        body {
          page-break-before: always;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }
        
        th {
          border-bottom: 3px solid #ED6E56 !important;
          font-size: 12px !important;
          font-weight: 200 !important;
          line-height: 110% !important;
          text-align: center !important;
          background-color: #FCFCFC !important;
        }
        
        td {
          border: 1px solid #F2F3F5 !important;
          font-size: 12px !important;
          line-height: 120% !important;
          text-align: center !important;
        }

        td:first-child {
          text-align: left !important;
        }

        th:first-child {
          text-align: left !important;
        }
        
        tr:nth-child(odd){
          background-color: #FFFFFF !important;
        }

        tr:nth-child(even){
          background-color: #FFFFFF !important;
        }

        tr td:first-child {
          border-left: 0 !important;
          border-right: 0 !important;
        }

        tr td:last-child {
          border-left: 0 !important;
          border-right: 0 !important;
        }

        tr th:first-child {
          border-left: 0 !important;
          border-right: 0 !important;
        }

        #pdfHoursBreakDown tr:nth-child(4) {
          background-color: #fae1d4 !important;
        }

        #pdfHoursBreakDown tr:nth-child(6) {
          background-color: #fae1d4 !important;
        }

        #pdfHoursBreakDown tr:nth-of-type(4) td:nth-of-type(2) {
          background-color: #f7c6ac !important;
        }

        #pdfHoursBreakDown tr:nth-of-type(6) td:nth-of-type(2) {
          background-color: #f7c6ac !important;
        }
        
        .actionHeader, .actionData {
          opacity: 0;
        }

        .cpd-my-hours__view-links {
          display: none;
        }

        .cpd-my-table__event-name-details {
          display: block;
        }

        .cpd-my-table__event-name {
          margin-bottom: 5px !important;
        }

        .pdf-cpd-my-hours__hours-details {
          height: 26px !important;
          display: flex !important;
          flex-direction: row !important;
          width: 100% !important;
          justify-content: space-between !important;
          margin-bottom: 30px !important;
        }

        .cpd-my-hours__user-details-key {
          font-weight: 600;
          font-size: 16px;
          color: #06243C;
        }

        .cpd-my-hours__user-details-value {
          font-weight: 300;
          font-size: 16px;
        }

        .centered-div {
          margin: auto;
          width: 80%;
        }

        .banner {
          height: 120px;
          width: 100%;
          background-image: linear-gradient(rgba(255,255,255,0.6), rgba(255,255,255,0.6)), url('https://images.ctfassets.net/19hllh2ssa3x/6IFLeTe85oDKQKfc6CbtnP/77e5c96e62f87c76b6fcadd9a1414339/pattern_orange.svg');
          background-size: cover;
          margin-bottom: 90px !important;
        }

        .logos {
          height: 120px;
          margin-right: 140px;
          background: linear-gradient(rgba(255,255,255,0.6), rgba(255,255,255,0.6));
        }

        .pdf-image {
          opacity: 0.3 !important;
          float: right !important;
          margin-right: 50px !important;
        }

        .pdf-header-description {
          margin-bottom: 40px !important;
        }

        .pdf-header {
          margin-bottom: 40px !important;
        }

      </style>
    </head>
    <body>
      <div class="banner"></div>
      <div class="logos">
        <img class="pdf-image" src="${ucdLogo}" />
        <img class="pdf-image" src="${logo}" />
      </div>
      <div class='centered-div'>
        <div class="pdf-header"><h1>CPD Hours Breakdown</h1></div>
        <div class="pdf-header-description"><p>This section is a breakdown of the CPD events and eCPD modules that you have completed and recorded.</p></div>
        <div class="pdf-cpd-my-hours__hours-details">${userDetailsHtml}</div>
        <table id="pdfHoursBreakDown">${cpdMyTableHtml}</table>
        <table>${hoursBreakDownTableHtml}</table>
      </div>
    </body>
    </html>`;

    const opt = {
      margin: 1,
      pagebreak: { avoid: 'tr' },
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        backgroundColor: "#fff",
        scale: window.devicePixelRatio,
        y: 0,
        x: 0,
        scrollY: 0,
        scrollX: 0,
        windowWidth: 1800,
        dpi: 300,
        letterRendering: true,
        useCORS: true
      },
      jsPDF: { unit: 'px', format: [1800, 1800], orientation: 'portrait', precision: 18 }
    };
    // Convert directly from the HTML content
    html2pdf().set(opt).from(htmlContent).save('document.pdf').catch(error => {
        console.error('PDF generation error:', error);
    });
  }

  almostFullBar(width, barElement) {
    if (width > 95) {
      barElement.style['width'] = `${width}%`;
      barElement.style['border-radius'] = '64px';
    }
    else {
      barElement.style['width'] = `${width}%`;
      barElement.style['border-radius'] = '64px';
    }
  }

  fullBar(barTextElement, barElement, formerValue, latterValue, endText) {
    barTextElement.innerHTML = `<span class="cpd-my-hours__hours-complete-recorded-hours">${formerValue}/</span><span class="cpd-my-hours__hours-complete-total-hours">${latterValue}</span> Required Hours complete`;
    barElement.style['border-radius'] = '40px';
    barElement.style['width'] = '100%';
  }

  setCategoryHours(completed, total) {
    this.categoryHoursOfHoursTarget.innerHTML = `<span class="cpd-my-hours__hours-complete-recorded-hours">${completed}/</span><span class="cpd-my-hours__hours-complete-total-hours">${total}</span> Categories complete`;
  }

  adjustYear(e){
    let year = parseInt(this.yearSelectorTarget.innerHTML) + e.params.value;
    let otherButton = e.target.nextElementSibling?.nextElementSibling || e.target.previousElementSibling?.previousElementSibling 
    if (this.years.includes(`${year}`)) {
      this.setYear(year);
      otherButton.style['display'] = 'block';
      if (this.years.includes(`${year + e.params.value}`) === false)
        e.target.style['display'] = 'none';
    }
  }

  previousYearCheck(year) {
    if(this.years.includes(`${year}`) === false) {
      this.yearDecrementorTarget.style['display'] = 'none';
    }
  }

  defaultOptions() {
    return {
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': $('meta[name=csrf-token]').attr('content')
      },
    }
  }

  openSlide(e) {
    const slideId = e.currentTarget.dataset.slideId;

    if(slideId === 'events') {
      const eventId = e.currentTarget.dataset.eventId;
      const year = e.currentTarget.dataset.eventYear;

      this.populateEventSlide(eventId, year);
    }

    let slide = document.getElementById(`${slideId}Slide`);
    slide.classList.add(`${slideId}-table-mobile-content--open`);
  }

  closeSlide(e){
    const slideId = e.currentTarget.dataset.slideId;
    let slide = document.getElementById(`${slideId}Slide`);
    slide.classList.remove(`${slideId}-table-mobile-content--open`);
  }
}
